import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { MockTestData } from '../../models/MockTestData';
import { 
  fetchTestSession, 
  TestSessionRequest, 
  batchCreateTestSeries, 
  BatchCreateTestSeriesRequest,
  TestSessionDTO
} from '../../services/testSessionService';
import { testSeriesConfig } from '../../data/seriesConfig';

// Helper functions for the slice
const getSubjectColor = (subject: string): string => {
  const subjectConfig = testSeriesConfig.flatMap(series => series.subjects).find(s => s.name === subject);
  return subjectConfig?.color || '#4CAF50'; // Default to green if not found
};

// Convert a session DTO to MockTestData format
const convertSessionDtoToMockTestData = (sessionDto: TestSessionDTO, subject: string): MockTestData => {
  // Format title from packageName if available
  let formattedTitle = sessionDto.packageName;
  if (formattedTitle) {
    formattedTitle = formattedTitle.replace(/^(Class-[0-9]+)\s*-\s*([^\s-]+)\s*-\s*/, '$1 $2 ');
  }

  // Extract series name from the package name (e.g., "Mock Test - 01")
  const seriesMatch = sessionDto.packageName?.match(/Mock Test - \d+/);
  const seriesName = seriesMatch ? seriesMatch[0] : null;
  const seriesConfig = seriesName ? testSeriesConfig.find(series => series.seriesName === seriesName) : undefined;
  const seriesIsUntimed = seriesConfig?.timed === false;

  // Determine status based on seriesConfig dates instead of backend status
  let testStatus: 'open' | 'upcoming' | 'closed' = 'open';
  if (seriesConfig) {
    const now = new Date();
    const openingDate = new Date(seriesConfig.openingDate);
    const closingDate = new Date(seriesConfig.closingDate);

    if (now < openingDate) {
      testStatus = 'upcoming';
    } else if (now > closingDate) {
      testStatus = seriesIsUntimed ? 'open' : 'closed';
    } else {
      testStatus = 'open';
    }
  }

  // Calculate progress percentage - handle division by zero
  const totalQuestions = sessionDto.totalNoOfQuestions || 0;
  const completedQuestions = sessionDto.answers ? sessionDto.answers.length : 0;
  const progress = totalQuestions > 0 ? (completedQuestions / totalQuestions) * 100 : 0;

  // Calculate relative date display for test deadlines using seriesConfig dates
  let nextTestIn: string | undefined;
  if (seriesConfig) {
    const today = new Date();

    if (testStatus === 'open') {
      const closingDate = new Date(seriesConfig.closingDate);
      closingDate.setHours(23, 59, 59, 999);
      const diffTime = closingDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      nextTestIn = diffDays <= 0 ? 'today' : `${diffDays} days`;
    } else if (testStatus === 'upcoming') {
      const openingDate = new Date(seriesConfig.openingDate);
      openingDate.setHours(0, 0, 0, 0);
      const diffTime = openingDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      nextTestIn = diffDays <= 0 ? 'today' : `${diffDays} days`;
    } else if (testStatus === 'closed') {
      nextTestIn = seriesIsUntimed ? 'Untimed test available' : 'closed';
    }
  } else {
    if (testStatus === 'open') {
      nextTestIn = '14 days';
    } else if (testStatus === 'upcoming') {
      nextTestIn = '7 days';
    }
  }

  return {
    sessionId: sessionDto.id,
    title: formattedTitle,
    subject,
    progress,
    color: getSubjectColor(subject),
    totalQuestions,
    completedQuestions,
    nextTestIn,
    timed: seriesConfig?.timed ?? true,
    status: testStatus,
    sessionStatus: sessionDto.status,
    timeLeft: sessionDto.timeLeft,
  };
};

// Define the test session state shape
interface TestSessionsState {
  sessionsBySeriesAndSubject: {
    [seriesName: string]: {
      [subject: string]: {
        data: MockTestData | null;
        status: 'idle' | 'loading' | 'succeeded' | 'failed';
        error: string | null;
      };
    };
  };
}

// Initialize the state with series and subjects from config
const initialState: TestSessionsState = {
  sessionsBySeriesAndSubject: testSeriesConfig.reduce((seriesAcc, series) => {
    seriesAcc[series.seriesName] = series.subjects.reduce((subjectAcc, subject) => {
      subjectAcc[subject.name] = {
        data: null,
        status: 'idle',
        error: null
      };
      return subjectAcc;
    }, {} as TestSessionsState['sessionsBySeriesAndSubject'][string]);
    return seriesAcc;
  }, {} as TestSessionsState['sessionsBySeriesAndSubject'])
};

// Create async thunk for fetching a test session
export const fetchTestSessionData = createAsyncThunk(
  'testSessions/fetchTestSessionData',
  async (params: TestSessionRequest) => {
    return await fetchTestSession(params);
  }
);

// Create async thunk for batch creating test sessions
export const batchCreateTestSessionData = createAsyncThunk(
  'testSessions/batchCreateTestSessionData',
  async (params: BatchCreateTestSeriesRequest) => {
    return await batchCreateTestSeries(params);
  }
);

// Create the slice
const testSessionsSlice = createSlice({
  name: 'testSessions',
  initialState,
  reducers: {
    clearTestSessions: (state) => {
      // Reset all test sessions to initial state
      Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
        Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
          state.sessionsBySeriesAndSubject[seriesName][subject] = {
            data: null,
            status: 'idle',
            error: null
          };
        });
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Individual test session fetch handlers
      .addCase(fetchTestSessionData.pending, (state, action) => {
        const { series, subject } = action.meta.arg;
        if (state.sessionsBySeriesAndSubject[series]?.[subject]) {
          state.sessionsBySeriesAndSubject[series][subject].status = 'loading';
        }
      })
      .addCase(fetchTestSessionData.fulfilled, (state, action) => {
        const { series, subject } = action.meta.arg;
        if (state.sessionsBySeriesAndSubject[series]?.[subject]) {
          state.sessionsBySeriesAndSubject[series][subject].status = 'succeeded';
          state.sessionsBySeriesAndSubject[series][subject].data = action.payload;
          state.sessionsBySeriesAndSubject[series][subject].error = null;
        }
      })
      .addCase(fetchTestSessionData.rejected, (state, action) => {
        const { series, subject } = action.meta.arg;
        if (state.sessionsBySeriesAndSubject[series]?.[subject]) {
          state.sessionsBySeriesAndSubject[series][subject].status = 'failed';
          state.sessionsBySeriesAndSubject[series][subject].error = action.error.message || 'Failed to fetch test session';
        }
      })
      
      // Batch test session creation handlers
      .addCase(batchCreateTestSessionData.pending, (state) => {
        // Mark all sessions as loading
        Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
          Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
            state.sessionsBySeriesAndSubject[seriesName][subject].status = 'loading';
          });
        });
      })
      .addCase(batchCreateTestSessionData.fulfilled, (state, action) => {
        // The response structure is { studentId, sessions: { [subject]: [sessionDto, ...] } }
        const response = action.payload;
        
        // Type guard to ensure response has sessions
        type TestSeriesResponse = {
          studentId: string;
          sessions: Record<string, TestSessionDTO[]>;
        };
        
        function isTestSeriesResponse(obj: any): obj is TestSeriesResponse {
          return obj && typeof obj === 'object' && 'sessions' in obj;
        }
        
        if (isTestSeriesResponse(response)) {
          // Process each subject's session array
          Object.entries(response.sessions).forEach(([subject, sessionDtoArray]) => {
            // Type assertion to help TypeScript understand the structure
            const sessions = sessionDtoArray as TestSessionDTO[];
            
            // Process each session in the array
            sessions.forEach((sessionDto: TestSessionDTO) => {
              // Extract series name from the session data
              const seriesName = sessionDto.series;
              
              
              // Convert the DTO to our MockTestData format
              const mockTestData = convertSessionDtoToMockTestData(sessionDto, subject);
              
              // Create series/subject structure if it doesn't exist, then update
              if (!state.sessionsBySeriesAndSubject[seriesName]) {
                state.sessionsBySeriesAndSubject[seriesName] = {};
              }
              if (!state.sessionsBySeriesAndSubject[seriesName][subject]) {
                state.sessionsBySeriesAndSubject[seriesName][subject] = {
                  data: null,
                  status: 'idle',
                  error: null
                };
              }

              state.sessionsBySeriesAndSubject[seriesName][subject] = {
                data: mockTestData,
                status: 'succeeded',
                error: null
              };
            });
          });
        } else {
          // A well-formed response that carries no sessions is still an outcome, and
          // leaving the statuses on the `loading` set by pending would spin forever.
          // Clear them so the UI can say something truthful.
          Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
            Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
              if (state.sessionsBySeriesAndSubject[seriesName][subject].status === 'loading') {
                state.sessionsBySeriesAndSubject[seriesName][subject].status = 'failed';
                state.sessionsBySeriesAndSubject[seriesName][subject].error =
                  'No test sessions were returned.';
              }
            });
          });
        }
      })
      .addCase(batchCreateTestSessionData.rejected, (state, action) => {
        // Mark all as failed with the error
        Object.keys(state.sessionsBySeriesAndSubject).forEach(seriesName => {
          Object.keys(state.sessionsBySeriesAndSubject[seriesName]).forEach(subject => {
            state.sessionsBySeriesAndSubject[seriesName][subject].status = 'failed';
            state.sessionsBySeriesAndSubject[seriesName][subject].error = 
              action.error.message || 'Failed to create test sessions in batch';
          });
        });
      });
  }
});

export const { clearTestSessions } = testSessionsSlice.actions;
export default testSessionsSlice.reducer;

// Selector to get a specific test session
export const selectTestSession = (state: { testSessions: TestSessionsState }, seriesName: string, subject: string) => {
  return state.testSessions.sessionsBySeriesAndSubject[seriesName]?.[subject] || { 
    data: null, 
    status: 'idle', 
    error: null 
  };
};
